import type { IPublicClientApplication } from '@azure/msal-browser';
import { isNotAssignedError } from './entitlement';

/** The slice of PublicClientApplication the startup redirect handling needs. */
export type StartupMsal = Pick<IPublicClientApplication, 'handleRedirectPromise' | 'setActiveAccount' | 'clearCache'>;

export interface StartupRedirectResult {
  /** True when Entra redirected back refusing the user with AADSTS50105 (Bug #19757). */
  accessDenied: boolean;
}

/**
 * Settle any pending MSAL redirect response at app start.
 *
 * Extracted from main.tsx so it can be tested (Bug #19757). Two outcomes of a rejected
 * `handleRedirectPromise`, deliberately NOT treated alike:
 *
 * - **AADSTS50105** — Entra refused the user (not assigned to the application). The portal
 *   requests the API scope at sign-in, so once "Assignment required" is on, an unassigned user is
 *   refused HERE. Clearing the cache and showing the login button again (the old behaviour) was a
 *   loop: click, be refused, come back to the button. It is a denial, and is reported as one.
 * - **anything else** — stale interaction state. The #14687 self-heal is kept exactly: clear the
 *   cache so the next sign-in attempt is not blocked.
 */
export async function handleStartupRedirect(msal: StartupMsal): Promise<StartupRedirectResult> {
  try {
    const response = await msal.handleRedirectPromise();
    if (response?.account) {
      msal.setActiveAccount(response.account);
    }
    return { accessDenied: false };
  } catch (error) {
    if (isNotAssignedError(error)) {
      // A decision, not stale state — nothing to clear, and clearing would not help: Entra would
      // refuse the same account again on the next sign-in.
      return { accessDenied: true };
    }
    // #14687 defect 2 — this block's comment claimed to clear stale interaction state; the body
    // only logged that it had. It cleared nothing, which is worse than an empty catch: anyone
    // reading the console line concluded recovery had run.
    //
    // A failed handleRedirectPromise leaves MSAL interaction state behind, and that state blocks
    // every subsequent sign-in attempt — the operator had to clear site data in DevTools by hand
    // to recover (verified 2026-08-10 after an 8-hour absence). Actually clearing it makes the app
    // self-heal on reload.
    try {
      await msal.clearCache();
      console.warn('[MSAL] redirect handling failed — cleared stale interaction state');
    } catch {
      // Nothing left to clear, or storage is unavailable. Either way the reload proceeds; this
      // must never stop the app from starting.
    }
    return { accessDenied: false };
  }
}
